import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import { LIVE_CHAT_ROOMS } from '../data';
import { getChapterForRoom, getNovaRoomTitle, getSpiralRiskForRoom } from '../data/novaCommunityConfig';
import { NovaLiveRoomCoHost } from '../components/NovaLiveRoomCoHost';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

// Mock live messages by room
const MOCK_MESSAGES_BY_ROOM = {
  'live-beta': [
    { id: 1, user: 'Sarah K.', content: "My clinic said they'll call by noon...", time: '5m ago', online: true },
    { id: 2, user: 'Jamie', content: 'Anyone else checking their phone every 30 seconds 😅', time: '2m ago', online: true },
  ],
  default: [
    { id: 1, user: 'Maya', content: 'Did anyone else feel super bloated on stim day 8?', time: '2m ago', online: true },
    { id: 2, user: 'Sarah K.', content: 'Yes! Mine peaked around day 9. Heating pad helped a lot.', time: '1m ago', online: true },
    { id: 3, user: 'Priya', content: 'Same here. Also drinking electrolyte mix — game changer.', time: 'Just now', online: true },
  ],
};

export default function LiveRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const room = LIVE_CHAT_ROOMS.find((r) => r.id === roomId)
    || LIVE_CHAT_ROOMS.find((r) => r.topic.toLowerCase().replace(/\s+/g, '-') === roomId);
  const [message, setMessage] = useState('');

  if (!room) {
    return (
      <DashboardAppShell fontFamily={FONT_BODY}>
        <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)' }}>Room not found</h1>
        <button type="button" onClick={() => navigate('/community')} style={{ marginTop: 16, padding: '10px 20px', background: 'var(--pink)', color: 'white', border: 'none', borderRadius: 999, cursor: 'pointer' }}>Back to Community</button>
      </DashboardAppShell>
    );
  }

  const novaRoomLabel = getNovaRoomTitle(room);
  const chapter = getChapterForRoom(room);
  const spiralRisk = getSpiralRiskForRoom(room);
  const mockMessages = MOCK_MESSAGES_BY_ROOM[room.id] || MOCK_MESSAGES_BY_ROOM.default;

  return (
    <DashboardAppShell fontFamily={FONT_BODY} mainStyle={{ display: 'flex', flexDirection: 'column', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <button type="button" onClick={() => navigate('/community')} style={{ fontSize: 14, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#FBEAF0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{room.icon}</div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 22, color: 'var(--text)', margin: 0 }}>{room.topic}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--sage)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sage)' }} /> {room.online} online
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#D4537E', fontWeight: 500 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#D4537E' }} /> Nova · {novaRoomLabel}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>always here</span>
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <NovaLiveRoomCoHost
            chapter={chapter}
            roomName={room.topic}
            spiralRisk={spiralRisk}
            onOpenNova={() => navigate('/nova')}
          />

          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mockMessages.map((m) => (
              <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ position: 'relative' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FBEAF0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--pink-d)', flexShrink: 0 }}>
                    {m.user.slice(0, 2).toUpperCase()}
                  </div>
                  {m.online && (
                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--sage)', border: '2px solid white' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{m.user}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{m.time}</span>
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>{m.content}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                style={{ flex: 1, padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, outline: 'none' }}
              />
              <button
                type="button"
                style={{ padding: '12px 20px', background: 'var(--pink)', color: 'white', border: 'none', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Send →
              </button>
            </div>
          </div>
        </div>
    </DashboardAppShell>
  );
}
